import { getCurrentUser } from "@vetty/api-sdk";
import { create } from "zustand";
import { User } from "@/types";
import { SESSION_CHECK_INTERVAL, TOKEN_REFRESH_INTERVAL } from "@/constants";
import { validateSession, refreshSession, axiosInstance } from "@/helpers";
import { stripAuthTokensFromClientPayload } from "@/helpers/strip-auth-tokens.helper";

/**
 * AuthStore Interface
 *
 * Defines the structure of the auth store.
 */
interface AuthStore {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isFetching: boolean;
  setUser: (user: User | null) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setIsFetching: (isFetching: boolean) => void;
  initializeAuth: () => Promise<void>;
  checkAuthSilently: () => Promise<void>;
  refreshAuthSilently: () => Promise<void>;
  handleAuthError: () => void;
  checkAuth: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

/**
 * useAuthStore Custom Hook
 *
 * Manages auth state for the application.
 */
export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  isFetching: true,
  setUser: (user) => set({ user }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  setIsFetching: (isFetching) => set({ isFetching }),

  initializeAuth: async () => {
    const session = await validateSession();
    let activeSession = session;

    if (!activeSession) {
      activeSession = await refreshSession();
    }

    if (!activeSession) {
      set({ isFetching: false });
      return;
    }

    const { data } = await getCurrentUser(axiosInstance);
    set({
      user: stripAuthTokensFromClientPayload(data.data) as User,
    });

    // if (
    //   typeof window !== "undefined" &&
    //   !window.location.pathname.includes("/verification") &&
    //   !data.data.onboardingCompleted
    // ) {
    //   // Router navigation will be handled by components that use this store
    //   return;
    // }

    set({
      isLoading: false,
      isFetching: false,
      isAuthenticated: true,
    });
  },

  checkAuthSilently: async () => {
    try {
      const session = await validateSession();
      if (!session) get().handleAuthError();
    } catch (error) {
      console.error("Silent auth check failed:", error);
    }
  },

  refreshAuthSilently: async () => {
    try {
      const session = await refreshSession();
      const { data } = await getCurrentUser(axiosInstance);

      if (session && data.success) {
        set({ user: data.data });
      } else {
        get().handleAuthError();
      }
    } catch (error) {
      console.error("Silent token refresh failed:", error);
      get().handleAuthError();
    }
  },

  handleAuthError: () => {
    set({
      user: null,
      isLoading: false,
      isFetching: false,
      isAuthenticated: false,
    });
  },

  checkAuth: async () => {
    await get().checkAuthSilently();
  },

  refreshAuth: async () => {
    await get().refreshAuthSilently();
  },
}));
